import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from './popover';
import { Dialog, DialogContent, DialogTitle, DialogClose } from './dialog';
import { INFO_CONTENT } from './info-content';
import { cn } from '../../lib/utils';

/**
 * Renders a string with `**bold**` and `- bullet` line prefixes.
 * No HTML in source — keeps i18n strings safe and readable.
 */
function renderBody(text) {
  if (!text) return null;
  const lines = text.split('\n').filter((l) => l.length > 0);
  return lines.map((line, i) => {
    const isBullet = line.startsWith('- ');
    const content = isBullet ? line.slice(2) : line;
    const segments = content.split(/(\*\*[^*]+\*\*)/g).map((seg, j) => {
      if (seg.startsWith('**') && seg.endsWith('**')) {
        return <strong key={j} className="text-foreground font-semibold">{seg.slice(2, -2)}</strong>;
      }
      return <React.Fragment key={j}>{seg}</React.Fragment>;
    });
    if (isBullet) {
      return (
        <div key={i} className="flex items-start gap-2 mt-2 first:mt-0">
          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-orange-500/70 shrink-0" />
          <span className="flex-1">{segments}</span>
        </div>
      );
    }
    return <p key={i} className="mt-2 first:mt-0">{segments}</p>;
  });
}

export default function InfoButton({ contentKey, className }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const entry = INFO_CONTENT[contentKey];

  if (!entry) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[InfoButton] Unknown contentKey: "${contentKey}"`);
    }
    return null;
  }

  const title = t(entry.titleKey);
  const body = t(entry.bodyKey);

  const triggerBtn = (
    <button
      type="button"
      aria-label={title}
      onClick={(e) => {
        e.stopPropagation();
        if (entry.kind === 'dialog') setOpen(true);
      }}
      className={cn(
        'inline-flex items-center justify-center p-1.5 rounded text-zinc-500 hover:text-zinc-300 dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-orange-500/50',
        className
      )}
    >
      <Info className="h-3.5 w-3.5" />
    </button>
  );

  if (entry.kind === 'popover') {
    return (
      <Popover>
        <PopoverTrigger asChild>{triggerBtn}</PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={6}
          collisionPadding={16}
          className="w-56 bg-zinc-900 border-zinc-800 text-sm text-white"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="font-semibold text-foreground">{title}</div>
          <div className="text-muted-foreground leading-relaxed mt-1">
            {renderBody(body)}
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <>
      {triggerBtn}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          compact
          hideCloseButton
          className="max-w-md w-[calc(100vw-2rem)] rounded-lg p-5 bg-zinc-900 border border-zinc-800"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-3">
            <div className="bg-orange-500/10 text-orange-400 rounded-full p-2 shrink-0">
              <Info className="h-4 w-4" />
            </div>
            <DialogTitle className="text-left text-foreground">{title}</DialogTitle>
          </div>
          <div className="text-sm text-muted-foreground leading-relaxed mt-3">
            {renderBody(body)}
          </div>
          <div className="mt-4 flex items-center justify-between gap-2">
            {entry.learnMoreHref ? (
              <a
                href={entry.learnMoreHref}
                onClick={() => setOpen(false)}
                className="text-sm text-orange-400 hover:text-orange-300"
              >
                {t('infoButton._learnMore')} →
              </a>
            ) : <span />}
            <DialogClose asChild>
              <button
                type="button"
                className="rounded-md bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium px-3 py-1.5"
              >
                {t('infoButton._gotIt')}
              </button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
